import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { useEffect, useState } from 'react';
import CreateReferral from './components/CreateReferral';
import { isProduction, getServiceUrl } from './utils/environment';
import './App.css';

function App() {
  const [selectedClinicLogo, setSelectedClinicLogo] = useState<any>(null);
  const [isMobile, setIsMobile] = useState(false);
  // Load clinic logo from localStorage
  const loadClinicLogo = () => {
    const storedClinic = localStorage.getItem('selectedClinic');
    if (storedClinic) {
      try {
        const clinic = JSON.parse(storedClinic);
        if (clinic.clinicLogo) {
          if (clinic.clinicLogo.data) {
            // Handle when clinicLogo has a data property (Buffer format)
            setSelectedClinicLogo(clinic.clinicLogo.data);
          } else {
            // Handle when clinicLogo is a direct string
            setSelectedClinicLogo(clinic.clinicLogo);
          }
        } else {
          setSelectedClinicLogo(null);
        }
      } catch (error) {
        console.error('Error parsing stored clinic:', error);
        setSelectedClinicLogo(null);
      }
    } else {
      setSelectedClinicLogo(null);
    }
  };

  useEffect(() => {
    // Disable right-click only in production
    if (isProduction()) {
      const handleContextMenu = (e: MouseEvent) => {
        e.preventDefault();
        return false;
      };
      
      document.addEventListener('contextmenu', handleContextMenu);
      
      return () => {
        document.removeEventListener('contextmenu', handleContextMenu);
      };
    }

    // Initial load of clinic logo
    loadClinicLogo();

    // Listen for localStorage changes from other components
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'selectedClinic') {
        loadClinicLogo();
      }
    };

    // Listen for custom events from CreateReferral component
    const handleClinicSelected = () => {
      loadClinicLogo();
    };

    const handleClinicCleared = () => {
      setSelectedClinicLogo(null);
    };

    // Listen for storage events (when localStorage changes in other tabs)
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('clinicSelected', handleClinicSelected);
    window.addEventListener('clinicCleared', handleClinicCleared);

    // Set up a polling mechanism to check for localStorage changes in the same tab
    const intervalId = setInterval(() => {
      loadClinicLogo();
    }, 1000); // Check every second

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('clinicSelected', handleClinicSelected);
      window.removeEventListener('clinicCleared', handleClinicCleared);
      clearInterval(intervalId);
    };
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    // Call handleResize initially to set the correct state on mount
    handleResize();
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  const handleBookAppointment = () => {
    let selectedClinic = '';
    selectedClinic = localStorage.getItem('selectedClinic') || '';
    const clinicCode = selectedClinic ? JSON.parse(selectedClinic).clinicCode : '';
    
    const url = getServiceUrl('booking', clinicCode);
    window.open(url, '_blank');
  }

  const handleTakeAssessment = () => {
    let selectedClinic = '';
    selectedClinic = localStorage.getItem('selectedClinic') || '';
    const clinicCode = selectedClinic ? JSON.parse(selectedClinic).clinicCode : '';
    
    const url = getServiceUrl('partner', clinicCode);
    window.open(url, '_blank');
  }

  // Helper function to render the clinic logo
  const renderClinicLogo = () => {
    if (!selectedClinicLogo) {
      console.log('No clinic logo available');
      return null;
    }
    
    try {
      console.log('Logo type:', typeof selectedClinicLogo);
      
      // Check if the logo is an array/buffer or a string
      if (typeof selectedClinicLogo === 'object' && selectedClinicLogo.length) {
        // It's a binary buffer
        console.log('Rendering as binary buffer');
        return `data:image/jpeg;base64,${btoa(String.fromCharCode(...new Uint8Array(selectedClinicLogo)))}`;
      } else if (typeof selectedClinicLogo === 'string') {
        // It's already a string (possibly base64 or URL)
        console.log('Rendering as string');
        return selectedClinicLogo;
      } else {
        console.log('Unknown logo format:', selectedClinicLogo);
      }
      return null;
    } catch (error) {
      console.error('Error rendering clinic logo:', error);
      return null;
    }
  };

  return (
    <>
      <div className="app-container flex flex-col min-h-screen">
        {/* Header */}
        <header className={`app-header w-full ${isMobile ? 'flex justify-between' : 'grid grid-cols-3'}`}>
        {/* Luna Logo - Left Column */}
        <div className="luna-logo-container flex justify-start">
          <img 
            src="/images/WhiteLogo.svg" 
            alt="Luna Logo" 
            className="luna-logo-header h-12 md:h-16" 
          />
        </div>
        
      {/* Navigation Links - Center Column */}
       {!isMobile && (
        <div className="nav-links flex items-center justify-center text-sm md:text-xl text-white">
          <p className="cursor-pointer"
              onClick={() => handleBookAppointment()}
              style={{color: "blue", textDecoration: "underline"}}>
              Book an Appointment
          </p>
          <p className="mx-2">|</p>
          <p className="cursor-pointer"
              onClick={() => handleTakeAssessment()}
              style={{color: "blue", textDecoration: "underline"}}>
              Take Assessment
          </p>
        </div>
       )}
        
        {/* Clinic Logo - Right Column */}
        <div className="clinic-logo-container flex justify-end">
          {selectedClinicLogo && renderClinicLogo() && (
            <img 
              src={renderClinicLogo() || ''}
              alt="Clinic Logo"
              className="clinic-logo h-12 md:h-16 w-auto object-contain"
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.style.display = 'none';
              }}
            />
          )}
        </div>
      </header>
      
      {/* Main Content */}
      {isMobile && (
          <div className="nav-links flex items-center justify-center text-sm md:text-xl text-white">
            <p className="cursor-pointer"
                onClick={() => handleBookAppointment()}
                style={{color: "blue", textDecoration: "underline"}}>
                Book an Appointment
            </p>
            <p className="mx-2">|</p>
            <p className="cursor-pointer"
                onClick={() => handleTakeAssessment()}
                style={{color: "blue", textDecoration: "underline"}}>
                Take Assessment
            </p>
          </div>
        )
      }
      <main className="app-content flex-grow">
        <Router>
          <Routes>
            <Route path="/" element={<CreateReferral />} />
            <Route path="/:subPath" element={<CreateReferral />} />
          </Routes>
        </Router>
      </main>

      {isMobile && (
        <img 
          src="/images/Hipaa.svg" 
          alt="HIPAA Compliant" 
          className="hipaa-logo-mobile h-12"
        />
      )}
    </div>
      
    {/* Footer */}
    {
      !isMobile && <img 
        src="/images/Hipaa.svg" 
        alt="HIPAA Compliant" 
        className="hipaa-logo-footer h-12 md:h-16 lg:h-24" 
      />
    }
  </>
  );
}

export default App;
